import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, PaymentRequestButtonElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import AedPrice from '../components/AedPrice';
import './Checkout.css';

// UAE Emirates list
const EMIRATES = [
  { id: 'abu_dhabi', key: 'abuDhabi' },
  { id: 'dubai', key: 'dubai' },
  { id: 'sharjah', key: 'sharjah' },
  { id: 'ajman', key: 'ajman' },
  { id: 'umm_al_quwain', key: 'umm_al_quwain' },
  { id: 'ras_al_khaimah', key: 'ras_al_khaimah' },
  { id: 'fujairah', key: 'fujairah' }
];

// Stripe publishable key from environment
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

// Card Element Styling
const cardStyle = {
  style: {
    base: {
      color: '#333',
      fontFamily: 'Cairo, Inter, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626'
    }
  },
  hidePostalCode: true
};

// Apple Pay / Google Pay Button Component
const WalletPaymentButton = ({
  total,
  shippingAddress,
  items,
  token,
  clearCart,
  navigate,
  t,
  setProcessing,
  setError,
  onWalletAvailable
}) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [walletAvailable, setWalletAvailable] = useState(false);

  useEffect(() => {
    if (!stripe) return;

    const pr = stripe.paymentRequest({
      country: 'AE',
      currency: 'aed',
      total: {
        label: 'Artisan Samosa Order',
        amount: Math.round(total * 100), // Convert to fils
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    });

    // Check if Apple Pay or Google Pay is available
    pr.canMakePayment().then(result => {
      if (result) {
        setPaymentRequest(pr);
        setWalletAvailable(true);
        onWalletAvailable && onWalletAvailable(true, result.applePay ? 'apple' : 'google');
      }
    });

    // Handle payment method event
    pr.on('paymentmethod', async (ev) => {
      setProcessing(true);

      try {
        // Create payment intent on backend
        const intentResponse = await fetch('/api/payments/create-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            amount: total,
            currency: 'aed'
          })
        });

        const { clientSecret, error: intentError } = await intentResponse.json();

        if (intentError) {
          ev.complete('fail');
          setError(intentError);
          setProcessing(false);
          return;
        }

        // Confirm the payment
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmError) {
          ev.complete('fail');
          setError(confirmError.message);
          setProcessing(false);
          return;
        }

        ev.complete('success');

        if (paymentIntent.status === 'succeeded') {
          // Create order
          const orderResponse = await fetch('/api/orders', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              items: items.map(item => ({
                productId: item.id,
                quantity: item.quantity
              })),
              shippingAddress,
              paymentIntentId: paymentIntent.id,
              paymentMethod: 'card',
              paymentStatus: 'paid'
            })
          });

          if (orderResponse.ok) {
            const order = await orderResponse.json();
            clearCart();
            navigate(`/orders/${order.id}?success=true`);
          } else {
            const data = await orderResponse.json();
            setError(data.error || t('orderFailed'));
            setProcessing(false);
          }
        }
      } catch (err) {
        ev.complete('fail');
        setError(err.message || 'Payment failed');
        setProcessing(false);
      }
    });
  }, [stripe, total]);

  // Update payment request amount when total changes
  useEffect(() => {
    if (paymentRequest) {
      paymentRequest.update({
        total: {
          label: 'Artisan Samosa Order',
          amount: Math.round(total * 100),
        },
      });
    }
  }, [paymentRequest, total]);

  if (!walletAvailable || !paymentRequest) {
    return null;
  }

  return (
    <div className="wallet-payment-section">
      <div className="wallet-button-container">
        <PaymentRequestButtonElement
          options={{
            paymentRequest,
            style: {
              paymentRequestButton: {
                type: 'default',
                theme: 'dark',
                height: '48px',
              },
            },
          }}
        />
      </div>
      <div className="payment-divider">
        <span>{t('orPayWithCard')}</span>
      </div>
    </div>
  );
};

// Stripe Card Form Component (only used inside Elements provider)
const StripeCardForm = ({ onCardChange, cardStyle, t, isRTL }) => {
  return (
    <div className="card-element-container">
      <label className="form-label">{t('cardDetails')}</label>
      <div className="card-element-wrapper" dir="ltr" style={{ textAlign: 'left' }}>
        <CardElement options={cardStyle} onChange={onCardChange} />
      </div>
      <p className="card-hint">
        Test card: 4242 4242 4242 4242, any future date, any CVC
      </p>
    </div>
  );
};

// Stripe Payment Handler Component (only used inside Elements provider)
const StripePaymentHandler = ({
  shippingAddress,
  items,
  getTotal,
  token,
  clearCart,
  navigate,
  t,
  setProcessing,
  setError,
  cardComplete,
  promoCode: appliedPromoCode
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleStripePayment = async () => {
    if (!stripe || !elements) {
      setError('Stripe not loaded');
      return false;
    }

    // Create payment intent on backend
    const intentResponse = await fetch('/api/payments/create-payment-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        amount: getTotal(),
        currency: 'aed'
      })
    });

    const { clientSecret, error: intentError } = await intentResponse.json();

    if (intentError) {
      setError(intentError);
      return false;
    }

    // Confirm card payment
    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement(CardElement),
        billing_details: {
          name: shippingAddress.fullName,
          phone: shippingAddress.phone
        }
      }
    });

    if (stripeError) {
      setError(stripeError.message);
      return false;
    }

    if (paymentIntent.status === 'succeeded') {
      // Create order with payment info
      const orderResponse = await fetch('/api/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: items.map(item => ({
            productId: item.id,
            quantity: item.quantity
          })),
          shippingAddress,
          paymentIntentId: paymentIntent.id,
          paymentMethod: 'card',
          paymentStatus: 'paid',
          promoCode: appliedPromoCode || undefined
        })
      });

      if (orderResponse.ok) {
        const order = await orderResponse.json();
        clearCart();
        navigate(`/orders/${order.id}?success=true`);
        return true;
      } else {
        const data = await orderResponse.json();
        setError(data.error || t('orderFailed'));
        return false;
      }
    }
    return false;
  };

  // Expose the payment handler
  React.useEffect(() => {
    window.handleStripePayment = handleStripePayment;
    return () => {
      delete window.handleStripePayment;
    };
  }, [stripe, elements, shippingAddress, items, token]);

  return null;
};

// Main Checkout Form Component
const CheckoutForm = ({
  shippingAddress,
  setShippingAddress,
  paymentMethod,
  setPaymentMethod,
  useStripePayment,
  promoCode: appliedPromoCode
}) => {
  const navigate = useNavigate();
  const { items, getTotal, clearCart } = useCart();
  const { token } = useAuth();
  const { t, language } = useLanguage();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);

  const formatPrice = (amount) => <AedPrice amount={amount} />;

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({ ...prev, [name]: value }));
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setProcessing(true);

    try {
      if (paymentMethod === 'card' && useStripePayment) {
        // Use Stripe payment
        if (window.handleStripePayment) {
          const success = await window.handleStripePayment();
          if (!success) {
            setProcessing(false);
          }
        } else {
          setError('Payment system not ready');
          setProcessing(false);
        }
      } else {
        // Cash on delivery
        const orderResponse = await fetch('/api/orders', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            items: items.map(item => ({
              productId: item.id,
              quantity: item.quantity
            })),
            shippingAddress,
            paymentIntentId: 'cod_' + Date.now(),
            paymentMethod: 'cod',
            paymentStatus: 'pending',
            promoCode: appliedPromoCode || undefined
          })
        });

        if (orderResponse.ok) {
          const order = await orderResponse.json();
          clearCart();
          navigate(`/orders/${order.id}?success=true`);
        } else {
          const data = await orderResponse.json();
          setError(data.error || t('orderFailed'));
          setProcessing(false);
        }
      }
    } catch (err) {
      setError(err.message || t('orderFailed'));
      setProcessing(false);
    }
  };

  const isFormValid = () => {
    const addressValid = shippingAddress.fullName && shippingAddress.phone &&
                         shippingAddress.address && shippingAddress.city &&
                         shippingAddress.emirate;
    if (paymentMethod === 'card' && useStripePayment) {
      return addressValid && cardComplete;
    }
    return addressValid;
  };

  return (
    <form onSubmit={handleSubmit} className="shipping-form">
      <h3>{t('shippingAddress')}</h3>

      <div className="form-group">
        <label className="form-label">{t('fullName')}</label>
        <input
          type="text"
          name="fullName"
          value={shippingAddress.fullName}
          onChange={handleAddressChange}
          className="form-input"
          required
        />
      </div>

      <div className="form-group">
        <label className="form-label">{t('phone')}</label>
        <input
          type="tel"
          name="phone"
          value={shippingAddress.phone}
          onChange={handleAddressChange}
          className="form-input"
          placeholder="+971 XX XXX XXXX"
          required
        />
      </div>

      <div className="form-group">
        <label className="form-label">{t('address')}</label>
        <input
          type="text"
          name="address"
          value={shippingAddress.address}
          onChange={handleAddressChange}
          className="form-input"
          required
        />
      </div>

      <div className="form-row form-row-2col">
        <div className="form-group">
          <label className="form-label">{t('city')}</label>
          <input
            type="text"
            name="city"
            value={shippingAddress.city}
            onChange={handleAddressChange}
            className="form-input"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">{t('emirate')}</label>
          <select
            name="emirate"
            value={shippingAddress.emirate}
            onChange={handleAddressChange}
            className="form-input"
            required
          >
            <option value="">{t('selectEmirate')}</option>
            {EMIRATES.map(emirate => (
              <option key={emirate.id} value={emirate.id}>
                {t(emirate.key)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Payment Method Selection */}
      <h3 style={{ marginTop: '32px' }}>{t('paymentMethod')}</h3>

      <div className="payment-methods">
        <label className={`payment-option ${paymentMethod === 'cod' ? 'selected' : ''}`}>
          <input
            type="radio"
            name="paymentMethod"
            value="cod"
            checked={paymentMethod === 'cod'}
            onChange={(e) => setPaymentMethod(e.target.value)}
          />
          <span className="payment-option-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg></span>
          <span className="payment-option-text">
            <strong>{t('cashOnDelivery')}</strong>
            <small>{t('payWhenReceive')}</small>
          </span>
        </label>

        {useStripePayment && (
          <label className={`payment-option ${paymentMethod === 'card' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="paymentMethod"
              value="card"
              checked={paymentMethod === 'card'}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
            <span className="payment-option-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg></span>
            <span className="payment-option-text">
              <strong>{t('creditCard')}</strong>
              <small>{t('securePayment')}</small>
            </span>
          </label>
        )}
      </div>

      {/* Apple Pay / Google Pay Button (only shown when card payment selected) */}
      {paymentMethod === 'card' && useStripePayment && (
        <WalletPaymentButton
          total={getTotal()}
          shippingAddress={shippingAddress}
          items={items}
          token={token}
          clearCart={clearCart}
          navigate={navigate}
          t={t}
          setProcessing={setProcessing}
          setError={setError}
        />
      )}

      {/* Card Element (only shown when card payment selected) */}
      {paymentMethod === 'card' && useStripePayment && (
        <StripeCardForm onCardChange={handleCardChange} cardStyle={cardStyle} t={t} />
      )}

      {/* Stripe Payment Handler (invisible, just handles payment logic) */}
      {useStripePayment && (
        <StripePaymentHandler
          shippingAddress={shippingAddress}
          items={items}
          getTotal={getTotal}
          token={token}
          clearCart={clearCart}
          navigate={navigate}
          t={t}
          setProcessing={setProcessing}
          setError={setError}
          cardComplete={cardComplete}
        />
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      <button
        type="submit"
        className="btn btn-primary btn-lg btn-block"
        disabled={processing || !isFormValid()}
      >
        {processing ? t('processing') : (
          paymentMethod === 'card' && useStripePayment
            ? <span>{t('payNow')} {formatPrice(getTotal())}</span>
            : t('placeOrder')
        )}
      </button>
    </form>
  );
};

// Wrapper for Stripe Elements
const StripeCheckoutForm = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm {...props} useStripePayment={true} />
    </Elements>
  );
};

// Main Checkout Component
const Checkout = () => {
  const navigate = useNavigate();
  const { items, isEmpty, getSubtotal, getShipping, getTotal, getDisplayPrice } = useCart();
  const { token } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [creditCardEnabled, setCreditCardEnabled] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    fullName: '',
    address: '',
    city: '',
    emirate: '',
    phone: '',
    country: 'UAE'
  });

  const formatPrice = (amount) => <AedPrice amount={amount} />;

  const getItemName = (item) => {
    return language === 'ar' && item.nameAr ? item.nameAr : item.name;
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const response = await fetch('/api/promo/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode, subtotal: getSubtotal() })
      });
      const result = await response.json();
      if (result.valid) {
        setPromoDiscount(result.discount);
        setAppliedPromo(result);
        setPromoError('');
      } else {
        setPromoDiscount(0);
        setAppliedPromo(null);
        const errorMessages = {
          invalidCode: t('invalidCode'),
          expiredCode: t('expiredCode'),
          codeMinOrder: `${t('codeMinOrder')}: ${formatPrice(result.minOrder || 0)}`
        };
        setPromoError(errorMessages[result.error] || t('invalidCode'));
      }
    } catch (error) {
      setPromoError(t('invalidCode'));
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoDiscount(0);
    setAppliedPromo(null);
    setPromoError('');
  };

  const getFinalTotal = () => {
    return getTotal() - promoDiscount;
  };

  // Fetch saved address and settings on load
  useEffect(() => {
    const fetchSavedAddress = async () => {
      try {
        const response = await fetch('/api/orders/saved-address', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const savedAddress = await response.json();
          if (savedAddress) {
            setShippingAddress(savedAddress);
          }
        }
      } catch (error) {
        console.error('Failed to fetch saved address:', error);
      }
    };

    const fetchSettings = async () => {
      try {
        const response = await fetch('/api/settings/public');
        if (response.ok) {
          const settings = await response.json();
          setCreditCardEnabled(settings.creditCardEnabled);
        } else {
          setCreditCardEnabled(false);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
        setCreditCardEnabled(false);
      } finally {
        setSettingsLoaded(true);
      }
    };

    if (token) {
      fetchSavedAddress();
    }
    fetchSettings();
  }, [token]);

  useEffect(() => {
    if (isEmpty) {
      navigate('/cart');
    }
  }, [isEmpty, navigate]);

  if (isEmpty) return null;

  if (!settingsLoaded) {
    return (
      <div className={`checkout-page ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container">
          <div className="page-header">
            <h1>{t('checkout')}</h1>
          </div>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            {t('loading') || 'Loading...'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`checkout-page ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container">
        <div className="page-header">
          <h1>{t('checkout')}</h1>
        </div>

        <div className="checkout-layout">
          <div className="checkout-form">
            {stripePromise && creditCardEnabled === true ? (
              <StripeCheckoutForm
                shippingAddress={shippingAddress}
                setShippingAddress={setShippingAddress}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                promoCode={appliedPromo ? appliedPromo.code : undefined}
              />
            ) : (
              <CheckoutForm
                shippingAddress={shippingAddress}
                setShippingAddress={setShippingAddress}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                useStripePayment={false}
                promoCode={appliedPromo ? appliedPromo.code : undefined}
              />
            )}
          </div>

          <div className="order-summary">
            <h3>{t('orderSummary')}</h3>

            <div className="summary-items">
              {items.map(item => (
                <div key={item.id} className="summary-item">
                  <div className="item-image-wrapper">
                    <div className="item-image">
                      {item.hideImage ? (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          background: '#f3f4f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px'
                        }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                        </div>
                      ) : (
                        <img src={item.image} alt={item.name} />
                      )}
                    </div>
                    <span className="item-qty">{item.quantity}</span>
                  </div>
                  <div className="item-info">
                    <span className="item-name">{getItemName(item)}</span>
                    <span className="item-price">{formatPrice(getDisplayPrice(item) * item.quantity)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Promo Code */}
            <div className="promo-code-section">
              {appliedPromo ? (
                <div className="promo-applied">
                  <span className="promo-applied-text">
                    ✓ {appliedPromo.code} ({appliedPromo.discountType === 'percentage' ? `${appliedPromo.discountValue}%` : formatPrice(appliedPromo.discountValue)})
                  </span>
                  <button onClick={handleRemovePromo} className="promo-remove-btn">{t('removeCode')}</button>
                </div>
              ) : (
                <div className="promo-input-row">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                    placeholder={t('promoCode')}
                    className="promo-input"
                    dir="ltr"
                  />
                  <button
                    onClick={handleApplyPromo}
                    className="btn btn-primary promo-apply-btn"
                    disabled={promoLoading || !promoCode.trim()}
                  >
                    {promoLoading ? '...' : t('applyCode')}
                  </button>
                </div>
              )}
              {promoError && <p className="promo-error">{promoError}</p>}
            </div>

            <div className="summary-totals">
              <div className="summary-row">
                <span>{t('subtotal')}</span>
                <span>{formatPrice(getSubtotal())}</span>
              </div>
              {promoDiscount > 0 && (
                <div className="summary-row discount-row">
                  <span>{t('discount')}</span>
                  <span className="discount-value">-{formatPrice(promoDiscount)}</span>
                </div>
              )}
              <div className="summary-row">
                <span>{t('shipping')}</span>
                <span>{formatPrice(getShipping())}</span>
              </div>
              <div className="summary-row total">
                <span>{t('total')}</span>
                <span>{formatPrice(getFinalTotal())}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
